import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Chip,
  Divider
} from '@mui/material';
import moment from 'moment';
import financeStore from '../stores/FinanceStore';

const EmployeeCommissionModal = observer(({ open, onClose, employeeId, employeeName }) => {
  const [selectedTransactions, setSelectedTransactions] = useState([]);

  useEffect(() => {
    if (open && employeeId) {
      financeStore.loadEmployeeReport(employeeId);
      setSelectedTransactions([]);
    }
  }, [open, employeeId]);

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const pendingTransactions = (financeStore.employeeReport?.transactions || [])
        .filter(t => t.status === 'confirmed')
        .map(t => t.id);
      setSelectedTransactions(pendingTransactions);
    } else {
      setSelectedTransactions([]);
    }
  };

  const handleSelectTransaction = (transactionId) => {
    setSelectedTransactions(prev => {
      if (prev.includes(transactionId)) {
        return prev.filter(id => id !== transactionId);
      } else {
        return [...prev, transactionId];
      }
    });
  };

  const handleMarkAsPaid = async () => {
    if (selectedTransactions.length === 0) return;

    const success = await financeStore.markAsPaid(selectedTransactions);
    if (success) {
      setSelectedTransactions([]);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const report = financeStore.employeeReport;
  const transactions = report?.transactions || [];
  const pendingTransactions = transactions.filter(t => t.status === 'confirmed');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Comissões - {employeeName}
        {report && (
          <Typography variant="subtitle2" color="text.secondary">
            {moment().month(report.period.month - 1).format('MMMM')} {report.period.year}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {financeStore.error && (
            <Alert severity="error" onClose={() => financeStore.clearMessages()} sx={{ mb: 2 }}>
              {financeStore.error}
            </Alert>
          )}

          {financeStore.successMessage && (
            <Alert severity="success" onClose={() => financeStore.clearMessages()} sx={{ mb: 2 }}>
              {financeStore.successMessage}
            </Alert>
          )}

          {financeStore.isLoading ? (
            <Typography>Carregando...</Typography>
          ) : report ? (
            <>
              {/* Summary */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 2, mb: 3 }}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Agendamentos
                  </Typography>
                  <Typography variant="h6">
                    {report.bookingCount}
                  </Typography>
                </Paper>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Total Serviços
                  </Typography>
                  <Typography variant="h6">
                    {formatCurrency(report.totalServicePrice)}
                  </Typography>
                </Paper>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Total Comissão
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {formatCurrency(report.totalCommissionAmount)}
                  </Typography>
                </Paper>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Transactions Table */}
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                Transações
              </Typography>

              {transactions.length === 0 ? (
                <Typography color="text.secondary">
                  Nenhuma transação neste período
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          {pendingTransactions.length > 0 && (
                            <Checkbox
                              indeterminate={
                                selectedTransactions.length > 0 &&
                                selectedTransactions.length < pendingTransactions.length
                              }
                              checked={
                                pendingTransactions.length > 0 &&
                                selectedTransactions.length === pendingTransactions.length
                              }
                              onChange={handleSelectAll}
                            />
                          )}
                        </TableCell>
                        <TableCell>Data</TableCell>
                        <TableCell align="right">Serviço</TableCell>
                        <TableCell align="right">Comissão %</TableCell>
                        <TableCell align="right">Valor</TableCell>
                        <TableCell align="center">Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transactions.map((transaction) => (
                        <TableRow key={transaction.id}>
                          <TableCell padding="checkbox">
                            {transaction.status === 'confirmed' && (
                              <Checkbox
                                checked={selectedTransactions.includes(transaction.id)}
                                onChange={() => handleSelectTransaction(transaction.id)}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            {moment(transaction.bookingDate).format('DD/MM/YYYY')}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(transaction.servicePrice)}
                          </TableCell>
                          <TableCell align="right">
                            {transaction.commissionPercent}%
                          </TableCell>
                          <TableCell align="right">
                            <strong>{formatCurrency(transaction.commissionAmount)}</strong>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={transaction.status === 'confirmed' ? 'Pendente' : 'Pago'}
                              size="small"
                              color={transaction.status === 'confirmed' ? 'warning' : 'success'}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          ) : (
            <Typography>Nenhum dado disponível</Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          Fechar
        </Button>
        {selectedTransactions.length > 0 && (
          <Button
            onClick={handleMarkAsPaid}
            variant="contained"
            disabled={financeStore.isLoading}
          >
            Marcar {selectedTransactions.length} como Pago
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
});

export default EmployeeCommissionModal;
